import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api, { formatApiError } from "@/lib/api";
import i18n from "@/i18n";

const AuthContext = createContext(null);

// Only these languages have full UI translations at the moment. Any other
// value stored on the user profile falls back to the default (Italian).
const SUPPORTED_UI_LANGS = ["it", "en"];

function applyLanguage(lang) {
  const normalized = SUPPORTED_UI_LANGS.includes(lang) ? lang : "it";
  if (i18n.language !== normalized) i18n.changeLanguage(normalized);
  if (typeof document !== "undefined") document.documentElement.lang = normalized;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = unauth, object = auth
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      applyLanguage(data?.language);
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser({
        id: data.id, email: data.email, name: data.name, role: data.role,
        phone: data.phone, language: data.language,
        must_change_password: data.must_change_password,
      });
      applyLanguage(data.language);
      return true;
    } catch (e) {
      setError(formatApiError(e));
      return false;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      console.warn("Logout request failed, clearing local session anyway.", e);
    }
    setUser(false);
  }, []);

  const value = useMemo(
    () => ({ user, error, login, logout, refresh, refreshUser: refresh }),
    [user, error, login, logout, refresh]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
